import { Box, Stack, useTheme } from "@mui/material";
import { useEffect, useMemo, useRef } from "react";
import { GlassInfoPanel } from "./GlassInfoPanel.tsx";
import type { MissionControlTab } from "./TenantListPanel";
import { motion } from "framer-motion";
import { MissionControlTenant } from "src/store/api/generatedApi";
import { RegionMapPane, RegionMapPaneHandle } from "./RegionMapPane";
import {
  REGION_LONGITUDE_BOUNDARY,
  US_CENTER,
  EU_CENTER,
  US_ZOOM,
  EU_ZOOM,
} from "./millMapConstants";

export const MillMap = ({
  tenants,
  selectedTenant,
  onSelectTenant,
  hoveredTenant,
  activeTab,
  onTabChange,
}: {
  tenants: MissionControlTenant[];
  selectedTenant: MissionControlTenant | null;
  onSelectTenant: (tenant: MissionControlTenant | null) => void;
  hoveredTenant: MissionControlTenant | null;
  activeTab: MissionControlTab;
  onTabChange: (tab: MissionControlTab) => void;
}) => {
  const theme = useTheme();
  const usPaneRef = useRef<RegionMapPaneHandle>(null);
  const euPaneRef = useRef<RegionMapPaneHandle>(null);

  const usTenants = useMemo(
    () => tenants.filter((t) => t.longitude < REGION_LONGITUDE_BOUNDARY),
    [tenants]
  );

  const euTenants = useMemo(
    () => tenants.filter((t) => t.longitude >= REGION_LONGITUDE_BOUNDARY),
    [tenants]
  );

  const showUs = usTenants.length > 0;
  const showEu = euTenants.length > 0;

  const highlightedTenantName =
    (selectedTenant ?? hoveredTenant)?.tenant_name ?? null;

  useEffect(() => {
    if (selectedTenant) {
      const isUs = selectedTenant.longitude < REGION_LONGITUDE_BOUNDARY;
      const targetRef = isUs ? usPaneRef : euPaneRef;
      targetRef.current?.focusTenant(selectedTenant);
    } else {
      usPaneRef.current?.fitToTenants({ duration: 800 });
      euPaneRef.current?.fitToTenants({ duration: 800 });
    }
  }, [selectedTenant]);

  return (
    <Box sx={{ position: "relative", flexGrow: 1, minWidth: 0, minHeight: 0 }}>
      <Stack direction="row" sx={{ height: "100%", width: "100%", gap: 0.5 }}>
        {showUs ? (
          <Box
            sx={{
              flex: 2,
              minWidth: 0,
              height: "100%",
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: theme.shadows[6],
            }}
          >
            <RegionMapPane
              ref={usPaneRef}
              tenants={usTenants}
              highlightedTenantName={highlightedTenantName}
              selectedTenant={selectedTenant}
              onSelectTenant={onSelectTenant}
              defaultCenter={US_CENTER}
              defaultZoom={US_ZOOM}
            />
          </Box>
        ) : null}
        {showEu ? (
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              height: "100%",
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: theme.shadows[6],
            }}
          >
            <RegionMapPane
              ref={euPaneRef}
              tenants={euTenants}
              highlightedTenantName={highlightedTenantName}
              selectedTenant={selectedTenant}
              onSelectTenant={onSelectTenant}
              defaultCenter={EU_CENTER}
              defaultZoom={EU_ZOOM}
            />
          </Box>
        ) : null}
      </Stack>

      {selectedTenant ? (
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: "spring", stiffness: 260, damping: 20 }}
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            pointerEvents: "auto",
          }}
        >
          <GlassInfoPanel
            tenant={selectedTenant}
            activeTab={activeTab}
            onTabChange={onTabChange}
            onClose={() => {
              onSelectTenant(null);
            }}
          />
        </motion.div>
      ) : null}
    </Box>
  );
};
